import { forwardRef, type ReactElement, type ReactNode, type Ref } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton } from 'react-aria-components';
import cx from 'clsx';
import styles from '../../primitives/button.module.css';
import { Spinner } from '../spinner';

/**
 * @deprecated Use `medium` or `small` instead
 */
type DeprecatedSize = 'large';

export interface BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger';
  size?: DeprecatedSize | 'medium' | 'small';
  iconOnly?: boolean;
  stretch?: boolean;
  /**
   * @deprecated Use `isPending` instead
   */
  isLoading?: boolean;
}

export interface ButtonProps extends AriaButtonProps, BaseButtonProps {
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'primary',
    size = 'medium',
    iconOnly = false,
    stretch = false,
    isLoading = false,
    isDisabled,
    className,
    ...props
  }: ButtonProps & { children: React.ReactNode },
  ref: Ref<HTMLButtonElement>
): ReactElement {
  const isIconOnlyLoading = iconOnly && (isLoading || props.isPending);

  return (
    <AriaButton
      className={cx(
        'body-1',
        styles.btn,
        variant !== 'primary' && styles[`btn--${variant}`],
        size !== 'medium' && styles[`btn--${size}`],
        iconOnly && styles['btn--icon-only'],
        stretch && styles[`btn--stretch`],
        className
      )}
      isDisabled={isLoading || isDisabled}
      ref={ref}
      {...props}
    >
      <div className={styles['button-wrapper']}>
        {!isIconOnlyLoading && children}
        {props.isPending || isLoading ? (
          <Spinner className={!iconOnly ? 'ml-8' : undefined} size="small" />
        ) : null}
      </div>
    </AriaButton>
  );
});

// Export styles as a separate named export to avoid Babel scope tracker issues
export const buttonStyles = styles;
